import { render } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { Home } from "./home";
import { clearResults, requestPersist, storageUsage } from "./lib/db";
import { getAccessCode, setAccessCode } from "./lib/jobs";
import { loadSamples } from "./lib/samples";
import { clearAllResults, init, useStore } from "./state";
import { Studio } from "./studio";
import { fmtBytes, Icon, Sheet, Toasts, toast } from "./ui";

interface Quota {
  used: number;
  limit: number;
}

interface Status {
  ok: boolean;
  provider: string | null;
  model: string | null;
  locked: boolean;
  serverCache: boolean;
  usage: { day: Quota; month: Quota } | null;
}

function useRoute(): string {
  const [hash, setHash] = useState(location.hash || "#/");
  useEffect(() => {
    const on = () => setHash(location.hash || "#/");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return hash;
}

function App() {
  const ready = useStore((s) => s.ready);
  const route = useRoute();
  const [status, setStatus] = useState<Status | null>(null);
  const [needCode, setNeedCode] = useState(false);
  const [settings, setSettings] = useState(false);

  useEffect(() => {
    void init();
    void loadSamples();
    void requestPersist();
    fetch("/api/status")
      .then((r) => r.json() as Promise<Status>)
      .then((s) => {
        setStatus(s);
        if (s.locked && !getAccessCode()) setNeedCode(true);
      })
      .catch(() => setStatus(null));
    const onAuth = () => setNeedCode(true);
    const onSettings = () => setSettings(true);
    window.addEventListener("jq:auth", onAuth);
    window.addEventListener("jq:settings", onSettings);
    return () => {
      window.removeEventListener("jq:auth", onAuth);
      window.removeEventListener("jq:settings", onSettings);
    };
  }, []);

  if (!ready) return <div class="boot" />;

  const m = route.match(/^#\/m\/([\w-]+)/);
  return (
    <>
      {m ? <Studio memberId={m[1]} status={status} /> : <Home status={status} />}
      <CodeGate open={needCode} onDone={() => setNeedCode(false)} />
      <Settings open={settings} onClose={() => setSettings(false)} status={status} />
      <Toasts />
    </>
  );
}

function CodeGate({ open, onDone }: { open: boolean; onDone: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await fetch("/api/auth", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ code }),
      });
      if (!r.ok) throw new Error("口令不对，再试一次");
      setAccessCode(code);
      onDone();
      toast("口令正确");
    } catch (e2) {
      setErr((e2 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!open) return null;
  return (
    <div class="gate">
      <form class="gate-card" onSubmit={submit}>
        <div class="brand brand-lg">
          <span class="brand-zh">剪前</span>
          <span class="brand-en">before the cut</span>
        </div>
        <p class="muted">这是家里自用的小工具，输入家庭口令后就能使用。</p>
        <input
          class="field"
          type="password"
          inputMode="text"
          autoComplete="current-password"
          placeholder="家庭口令"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          autoFocus
        />
        {err && <p class="err">{err}</p>}
        <button class="btn primary block" disabled={!code || busy}>
          {busy ? "验证中…" : "进入"}
        </button>
      </form>
    </div>
  );
}

function Settings({ open, onClose, status }: { open: boolean; onClose: () => void; status: Status | null }) {
  const [usage, setUsage] = useState<{ usage: number; quota: number } | null>(null);
  const [quota, setQuota] = useState<Status["usage"]>(status?.usage ?? null);
  useEffect(() => {
    if (!open) return;
    void storageUsage().then(setUsage);
    fetch("/api/status")
      .then((r) => r.json() as Promise<Status>)
      .then((s) => setQuota(s.usage))
      .catch(() => {});
  }, [open]);
  const fmtQuota = (q: Quota) => (q.limit > 0 ? `${q.used} / ${q.limit} 张` : `${q.used} 张（不限）`);
  const clear = useCallback(async () => {
    if (!confirm("清空所有已生成的发型？照片和家人会保留。")) return;
    await clearResults();
    await clearAllResults();
    setUsage(await storageUsage());
    toast("已清空发型缓存");
  }, []);
  return (
    <Sheet open={open} onClose={onClose} title="设置">
      <div class="settings">
        <div class="row">
          <span>生图模型</span>
          <span class="muted">{status?.ok ? `${status.provider} · ${status.model}` : "未配置"}</span>
        </div>
        {status?.provider === "seedream" && <ModelCheck />}
        {quota && (
          <>
            <div class="row">
              <span>今天已生成</span>
              <span class="muted">{fmtQuota(quota.day)}</span>
            </div>
            <div class="row">
              <span>本月已生成</span>
              <span class="muted">{fmtQuota(quota.month)}</span>
            </div>
          </>
        )}
        <div class="row">
          <span>本机已用空间</span>
          <span class="muted">{usage ? fmtBytes(usage.usage) : "—"}</span>
        </div>
        <div class="row">
          <span>五官锁定</span>
          <span class="muted">始终开启 · 脸部使用原图像素</span>
        </div>
        {status?.locked && (
          <button
            class="btn ghost block"
            onClick={() => {
              setAccessCode("");
              onClose();
              window.dispatchEvent(new CustomEvent("jq:auth"));
            }}
          >
            <Icon name="lock" size={18} /> 重新输入口令
          </button>
        )}
        <button class="btn ghost block danger" onClick={clear}>
          <Icon name="trash" size={18} /> 清空发型缓存
        </button>
        <p class="fine">
          照片保存在这台设备上。生成发型时会把当前照片上传给生图模型
          {status?.serverCache ? "，生成结果会在你自己的 Cloudflare R2 里存一份，换设备也能秒出" : ""}。
        </p>
      </div>
    </Sheet>
  );
}

type Check = { model: string; status: "ok" | "no_access" | "bad_key" | "error"; detail: string };

const CHECK_LABEL: Record<Check["status"], string> = {
  ok: "✓ 可以用",
  no_access: "✗ 调不到",
  bad_key: "✗ Key 无效",
  error: "？ 其它错误",
};

/** 检测当前 Key 能调哪些 Seedream 版本（服务端故意传非法尺寸，不出图、不计费） */
function ModelCheck() {
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState<Check[] | null>(null);
  const [extra, setExtra] = useState("");
  const run = async () => {
    setBusy(true);
    try {
      const r = await fetch("/api/diagnose", {
        method: "POST",
        headers: { "content-type": "application/json", "x-access-code": getAccessCode() },
        body: JSON.stringify({ extra: extra.trim() ? [extra.trim()] : [] }),
      });
      const body = (await r.json()) as { models?: Check[]; error?: string };
      if (!r.ok || !body.models) throw new Error(body.error ?? `检测失败（${r.status}）`);
      setList(body.models);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div class="model-check">
      <div class="custom-row">
        <input
          class="field"
          placeholder="其它模型 ID 或接入点 ep-…（可不填）"
          value={extra}
          onInput={(e) => setExtra((e.target as HTMLInputElement).value)}
        />
        <button class="btn ghost" onClick={run} disabled={busy}>
          {busy ? "检测中…" : "检测模型"}
        </button>
      </div>
      {list && (
        <ul>
          {list.map((c) => (
            <li class={c.status} title={c.detail}>
              <code>{c.model}</code>
              <span>{CHECK_LABEL[c.status]}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function mount() {
  render(<App />, document.getElementById("app")!);
}
